import React, { useEffect, useState } from 'react';
import { Box, Button, CircularProgress, IconButton, InputBaseProps, List, ListItem, ListItemIcon, ListItemText } from '@material-ui/core';
import { Add, ArrowBack } from '@material-ui/icons';
import { useTranslation } from 'react-i18next';
import { DeliveryListStockCardsQuery, useDeliveryListStockCardsLazyQuery } from '../../../../../../../generated/graphql';
import { useWarehousesScope } from '../../../../../../warehouse/providers/WarehouseScopeProvider';
import { TextField } from '../../../../../../../ui-kit';
import { useDebounce } from '../../../../../../../utils';
import { DeliveryListFormValues, StockCard } from '../../../types';
import { PickedCount } from '../PickedCount';
import StockCardOption from './StockCardOption';

const LIMIT = 10;

interface StockCardSearchMobileProps {
    onClose(): void;
    onAdd(stockCard: StockCard): void;
    onRemove(stockCardId: string): void;
    pickedStockCards: DeliveryListFormValues['deliveryListItems'];
    onCreateNewClick(): void;
    passedSearchValue?: string;
}

const StockCardSearchMobile = ({
    onClose,
    onAdd,
    onRemove,
    pickedStockCards,
    onCreateNewClick,
    passedSearchValue,
}: StockCardSearchMobileProps) => {
    const [offset, setOffset] = useState(0);
    const [searchQuery, setSearchQuery] = useState(passedSearchValue || '');
    const [hasMore, setHasMore] = useState(true);
    const [isFetchingMore, setIsFetchingMore] = useState(false);
    const { currentWarehouseId } = useWarehousesScope();
    const [getData, { data, fetchMore, loading }] = useDeliveryListStockCardsLazyQuery({ nextFetchPolicy: 'cache-first' });

    const { t } = useTranslation();

    const debouncedSearchQuery = useDebounce(searchQuery);
    let stockCards: DeliveryListStockCardsQuery['stockCards'] = data ? data.stockCards : [];

    useEffect(() => {
        if(stockCards.length === 0 || stockCards.length % LIMIT !== 0){
            setHasMore(false);
        } else {
            setHasMore(true);
        }
    }, [stockCards]);

    useEffect(() => {
        setOffset(0);
        getData({ variables: { nameOrSku: debouncedSearchQuery, warehouseIds: [currentWarehouseId as string], offset: 0, limit: LIMIT } });
    }, [debouncedSearchQuery, getData, currentWarehouseId]);

    useEffect(() => {
        if(passedSearchValue){
            setSearchQuery(passedSearchValue);
        }
    }, [passedSearchValue]);

    const handleFetchMore = async () => {
        if(fetchMore){
            const newOffset = offset + LIMIT;
            setIsFetchingMore(true);
            const { data: newData } = await fetchMore({ variables: { offset: newOffset } });
            setIsFetchingMore(false);
            setOffset(newOffset);
            ({ stockCards } = newData);
            if(stockCards.length < LIMIT){
                setHasMore(false);
            }
        }

    };
    if(data){
        ({ stockCards } = data);
    }

    const handleInputChange: InputBaseProps['onChange'] = (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { value } = event.target;
        setSearchQuery(value);
    };

    const shouldShowLoader = loading && stockCards.length === 0;

    return (
        <Box px={3} py={2}>
            <IconButton edge="start" color="inherit" onClick={onClose} aria-label="close">
                <ArrowBack />
            </IconButton>
            <Box mb={2} />
            <TextField
                name='search'
                label={t(`Type a name or SKU`)}
                fullWidth
                value={searchQuery}
                onChange={handleInputChange}
            />
            <PickedCount count={pickedStockCards.length} />
            <List>
                <ListItem
                    button
                    onClick={onCreateNewClick}
                >
                    <ListItemIcon>
                        <Add color='primary' />
                    </ListItemIcon>
                    <ListItemText primary={t('New warehouse card')} />
                </ListItem>
                {stockCards.map(stockCard => {
                    const handleAdd = () => onAdd(stockCard);
                    const handleRemove = () => onRemove(stockCard.id);
                    return (
                        <StockCardOption
                            key={stockCard.id}
                            isPicked={!!pickedStockCards.find(v => v.stockCardId === stockCard.id)}
                            onAdd={handleAdd}
                            onRemove={handleRemove}
                            stockCard={stockCard}
                        />
                    );
                })}
                {shouldShowLoader
                    ? (
                        <ListItem>
                            <Box justifyContent='center' display='flex' flex={1}>
                                <CircularProgress />
                            </Box>
                        </ListItem>
                    ) : null}
            </List>
            {hasMore ? (
                <Button
                    endIcon={isFetchingMore ? <CircularProgress size={16} /> : null}
                    disabled={isFetchingMore}
                    fullWidth
                    onClick={handleFetchMore}
                >
                    {t('Show more')}
                </Button>
                ) : null}
        </Box>
    );
};

export default StockCardSearchMobile;
